import React, { forwardRef, useCallback, useId, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { NumberFieldProps } from "./types";
import { classNames } from "helpers";
import Tooltip from "ui/components/Tooltip/Tooltip";
import { ReactSVG } from "react-svg";

const TYPING_DEBOUNCE_TIME = 500;

/**
 * Pročišćava upisano u nešto što se još može parsirati.
 *
 * Nedopušteni znakovi se ne primaju umjesto da se ispravljaju, pa kursor ostaje gdje jest:
 * React ponovno iscrta istu vrijednost i pozicija se ne pomiče.
 */
const sanitize = (
  raw: string,
  decimals: number,
  allowNegative: boolean,
  maxLength?: number
): string => {
  const isNegative = allowNegative && raw.trimStart().startsWith("-");
  const sign = isNegative ? "-" : "";

  // Ostaju samo znamenke i separatori; razmaci i valuta ispadaju.
  const cleaned = raw.replace(/[^\d.,]/g, "");

  // Cijelo polje bez decimala: separator nema značenje, pa se miče.
  if (decimals === 0) {
    const digits = cleaned.replace(/[.,]/g, "");

    return `${sign}${maxLength ? digits.slice(0, maxLength) : digits}`;
  }

  /*
    Zadnji separator je decimalni, raniji su grupiranje tisućica. Bez toga bi zalijepljeno
    „1.234,56" ispalo 1,56, jer bi se prva točka pročitala kao decimalna.
  */
  const lastSeparator = Math.max(cleaned.lastIndexOf(","), cleaned.lastIndexOf("."));
  const hasSeparator = lastSeparator >= 0;

  const wholePart = (hasSeparator ? cleaned.slice(0, lastSeparator) : cleaned).replace(
    /[.,]/g,
    ""
  );
  const fraction = hasSeparator
    ? cleaned.slice(lastSeparator + 1).replace(/[.,]/g, "").slice(0, decimals)
    : "";

  // Ograničenje se broji u znamenkama, pa decimale jedu mjesto cijelom dijelu.
  const whole = maxLength
    ? wholePart.slice(0, Math.max(0, maxLength - fraction.length))
    : wholePart;

  return hasSeparator ? `${sign}${whole}.${fraction}` : `${sign}${whole}`;
};

/** Tekst u broj. Prazno, sam minus ili sama točka još nisu broj, pa daju `null`. */
const parse = (text: string): number | null => {
  if (text === "" || text === "-" || text === "." || text === "-.") return null;

  const parsed = Number(text);

  return Number.isNaN(parsed) ? null : parsed;
};

/** Broj u tekst za prikaz izvan fokusa: grupiranje tisućica i fiksni broj decimala. */
const format = (value: number, decimals: number): string =>
  new Intl.NumberFormat("hr-HR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);

const NumberField = forwardRef<HTMLInputElement, NumberFieldProps>(
  (
    {
      id,
      value,
      onChange,
      name,
      label,
      placeholder,
      disableDebounce = false,
      debounceTime,
      disabled,
      required,
      error,
      startIcon,
      endIcon,
      size,
      cssClassName,
      onClick,
      groupAppend,
      groupPrepend,
      decimals = 2,
      allowNegative = false,
      min,
      max,
      maxLength,
    },
    ref
  ) => {
    const isError = !!error;

    const generatedId = useId();
    const inputId = id ?? `number-field-${generatedId}`;

    /**
     * Ono što korisnik trenutno tipka. `null` znači da se ne tipka, pa prikaz dolazi iz
     * `value`. Time vanjska promjena vrijednosti ne pregazi unos u tijeku.
     */
    const [text, setText] = useState<string | null>(null);

    const emitChange = useDebouncedCallback(
      (next: number | null) => onChange?.(next, name),
      debounceTime ?? TYPING_DEBOUNCE_TIME
    );

    const emit = useCallback(
      (next: number | null) => {
        if (disableDebounce) {
          onChange?.(next, name);
          return;
        }

        emitChange(next);
      },
      [disableDebounce, emitChange, name, onChange]
    );

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const clean = sanitize(e.target.value, decimals, allowNegative, maxLength);

        setText(clean.replace(".", ","));
        emit(parse(clean));
      },
      [allowNegative, decimals, emit, maxLength]
    );

    const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
      e.target.select();
    }, []);

    /** Granice se primjenjuju tek na izlasku: usput bi „10" u polju s minimumom 100 skakalo. */
    const handleBlur = useCallback(() => {
      emitChange.flush();

      const current = parse((text ?? "").replace(",", "."));

      if (current !== null) {
        const clamped = Math.min(max ?? current, Math.max(min ?? current, current));

        if (clamped !== current) {
          onChange?.(clamped, name);
        }
      }

      setText(null);
    }, [emitChange, max, min, name, onChange, text]);

    const displayValue =
      text ?? (value === null || value === undefined ? "" : format(value, decimals));

    const sizeClassName = () => {
      switch (size) {
        case "min":
          return "w-24";
        case "small":
          return "w-48";
        case "medium":
          return "w-96";
        case "large":
        case "full":
        default:
          return "w-full";
      }
    };

    const requiredClassName = () => {
      if (disabled) {
        return "ring-1 ring-slate-300 bg-gray-100";
      }
      if (isError && required) {
        // `focus:!ring-red-500` jer bi inače focus ring iz commonClassName pregazio crveni obrub.
        return "ring-1 ring-red-500 bg-red-50 text-red-900 placeholder-red-700 focus:!ring-red-500";
      }
      return "ring-1 ring-slate-300 bg-slate-50";
    };

    const commonClassName = () =>
      "relative rounded-sm py-2.5 pl-3 pr-3 text-left text-gray-800 placeholder:text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-slate-400 sm:text-sm sm:leading-6 z-0 disabled:cursor-default disabled:text-gray-900 disabled:border-gray-300 disabled:shadow-none";

    return (
      <>
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm mb-2 font-medium leading-6 text-[#344054]"
          >
            {label}
            {required && <span className="text-red-500"> *</span>}
          </label>
        )}

        <div className="flex rounded-sm relative">
          {groupPrepend && (
            <div className="absolute top-2/4 -translate-y-1/2 left-2 text-sm text-gray-500">
              {groupPrepend}
            </div>
          )}

          {startIcon && (
            <span className="absolute top-2/4 -translate-y-1/2 left-2 z-10">
              <ReactSVG src={startIcon} className="w-4 h-4 stroke-[#667085]" />
            </span>
          )}

          <input
            ref={ref}
            id={inputId}
            type="text"
            inputMode={decimals > 0 ? "decimal" : "numeric"}
            name={name}
            disabled={disabled}
            value={displayValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onClick={onClick}
            className={classNames(
              startIcon && "pl-8",
              endIcon && "pr-8",
              commonClassName(),
              sizeClassName(),
              requiredClassName(),
              cssClassName
            )}
            autoComplete="off"
            placeholder={placeholder}
            required={required}
            aria-invalid={isError}
            data-label={label}
          />

          {endIcon && (
            <span className="absolute top-2/4 -translate-y-1/2 right-2 z-10">
              <ReactSVG src={endIcon} className="w-4 h-4 stroke-[#667085]" />
            </span>
          )}

          {groupAppend && (
            <div className="absolute right-2 top-2/4 -translate-y-1/2 text-sm text-gray-500">
              {groupAppend}
            </div>
          )}

          {isError && (
            <div className="absolute right-2 top-2/4 -translate-y-1/2">
              <Tooltip description={error} />
            </div>
          )}
        </div>
      </>
    );
  }
);

export default React.memo(NumberField);
